import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import MedicalReviewByline from '../components/MedicalReviewByline';
import { CONTENT_HUBS, HUB_ORDER } from '../data/contentHubs';
import {
  ChevronRight, ArrowRight, Stethoscope, BookOpen, Calculator,
  Activity, HeartPulse, Baby, Dumbbell, HelpCircle,
} from 'lucide-react';

const ICONS = { Activity, HeartPulse, Baby, Dumbbell };

const ContentHubPage = () => {
  const { hubSlug } = useParams();
  const hub = CONTENT_HUBS[hubSlug];
  if (!hub) return <Navigate to="/resources" replace />;

  const Icon = ICONS[hub.icon] || Activity;
  const otherHubs = HUB_ORDER.filter((s) => s !== hub.slug).map((s) => CONTENT_HUBS[s]);
  const url = `/hub/${hub.slug}`;

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>{hub.metaTitle} | TCUBE FITNESS™</title>
        <meta name="description" content={hub.metaDescription} />
        <link rel="canonical" href={`https://tcubefitness.com${url}`} />
      </Helmet>

      <SEOSchema
        type="webpage"
        name={hub.title}
        description={hub.metaDescription}
        url={url}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Health Hubs', path: '/resources' },
          { name: hub.title, path: url },
        ]}
        faq={hub.faqs}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16" data-testid={`hub-page-${hub.slug}`}>
        <div className="container mx-auto px-4 sm:px-6">

          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 text-xs sm:text-sm">
            <ol className="flex items-center gap-1.5 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li><Link to="/resources" className="hover:text-white">Health Hubs</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li className="text-slate-300">{hub.title}</li>
            </ol>
          </nav>

          {/* Hero */}
          <section className="max-w-3xl mx-auto text-center mb-14 sm:mb-16">
            <span className={`inline-flex items-center gap-2 px-4 py-2 ${hub.badgeBg} border ${hub.badgeBorder} rounded-full ${hub.accent} text-sm font-medium mb-5`}>
              <Icon className="w-4 h-4" aria-hidden="true" />
              {hub.eyebrow}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              <span className={`bg-gradient-to-r ${hub.gradient} bg-clip-text text-transparent`}>{hub.title}</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-6">{hub.intro}</p>
            <div className="flex justify-center">
              <MedicalReviewByline />
            </div>
          </section>

          {/* Programs */}
          <section className="max-w-5xl mx-auto mb-14" aria-labelledby="hub-programs-heading">
            <div className="flex items-center gap-2.5 mb-6">
              <Stethoscope className={`w-5 h-5 ${hub.accent}`} aria-hidden="true" />
              <h2 id="hub-programs-heading" className="text-2xl sm:text-3xl font-bold text-white">Programs</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {hub.programs.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="group bg-slate-900/60 border border-slate-800 hover:border-slate-600 rounded-2xl p-6 transition-all"
                  data-testid={`hub-program-${p.to.replace(/\//g, '')}`}
                >
                  <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    {p.label}
                    <ArrowRight className={`w-4 h-4 ${hub.accent} opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all`} aria-hidden="true" />
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </Link>
              ))}
            </div>
          </section>

          {/* Articles + Calculators */}
          <section className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-6 mb-14">
            {/* Articles */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-7">
              <div className="flex items-center gap-2.5 mb-5">
                <BookOpen className={`w-5 h-5 ${hub.accent}`} aria-hidden="true" />
                <h2 className="text-xl sm:text-2xl font-bold text-white">Guides & Articles</h2>
              </div>
              <ul className="space-y-2.5">
                {hub.articles.map((a) => (
                  <li key={a.to}>
                    <Link to={a.to} className="text-slate-300 hover:text-white text-sm sm:text-base inline-flex items-start gap-2 group" data-testid={`hub-article-${a.to.split('/').pop()}`}>
                      <ArrowRight className={`w-4 h-4 mt-0.5 flex-shrink-0 ${hub.accent}`} aria-hidden="true" />
                      <span className="group-hover:underline underline-offset-4">{a.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Calculators */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-7">
              <div className="flex items-center gap-2.5 mb-5">
                <Calculator className={`w-5 h-5 ${hub.accent}`} aria-hidden="true" />
                <h2 className="text-xl sm:text-2xl font-bold text-white">Free Tools</h2>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {hub.calculators.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="inline-flex items-center gap-1.5 bg-slate-800/70 hover:bg-slate-700 border border-slate-700 rounded-full px-3.5 py-2 text-slate-200 text-sm transition-colors"
                    data-testid={`hub-calc-${c.to.split('/').pop()}`}
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
              {hub.tool && (
                <Link to={hub.tool.to} className={`mt-5 inline-flex items-center gap-2 ${hub.accent} hover:underline underline-offset-4 text-sm font-semibold`}>
                  {hub.tool.label}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              )}
            </div>
          </section>

          {/* FAQ */}
          {hub.faqs?.length > 0 && (
            <section className="max-w-3xl mx-auto mb-14" aria-labelledby="hub-faq-heading">
              <div className="flex items-center gap-2.5 mb-6">
                <HelpCircle className={`w-5 h-5 ${hub.accent}`} aria-hidden="true" />
                <h2 id="hub-faq-heading" className="text-2xl sm:text-3xl font-bold text-white">Frequently Asked Questions</h2>
              </div>
              <div className="space-y-4">
                {hub.faqs.map((f, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 sm:p-6" data-testid={`hub-faq-${i}`}>
                    <h3 className="text-white font-semibold text-base sm:text-lg mb-2">{f.question}</h3>
                    <p className="text-slate-400 text-sm sm:text-base leading-relaxed">{f.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className={`max-w-4xl mx-auto mb-14 bg-gradient-to-br ${hub.gradient} rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden`}>
            <div className="absolute inset-0 bg-slate-950/75" aria-hidden="true"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Not sure where to start?</h2>
              <p className="text-slate-300 mb-6 max-w-xl mx-auto text-sm sm:text-base">
                Book a free consultation and our medical team will guide you to the right starting point for your health.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/book-consultation?from=hub-${hub.slug}`} className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all" data-testid="hub-cta-book">
                  Book Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link to="/find-my-program" className="inline-flex items-center justify-center gap-2 bg-slate-800/80 hover:bg-slate-700 text-white px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all" data-testid="hub-cta-finder">
                  Find My Program
                </Link>
              </div>
            </div>
          </section>

          {/* Other hubs */}
          <section className="max-w-5xl mx-auto" aria-labelledby="other-hubs-heading">
            <h2 id="other-hubs-heading" className="text-lg font-bold text-white mb-4">Explore other health hubs</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {otherHubs.map((h) => {
                const HIcon = ICONS[h.icon] || Activity;
                return (
                  <Link key={h.slug} to={`/hub/${h.slug}`} className="group bg-slate-900/50 border border-slate-800 hover:border-slate-600 rounded-2xl p-5 transition-all" data-testid={`other-hub-${h.slug}`}>
                    <HIcon className={`w-6 h-6 ${h.accent} mb-3`} aria-hidden="true" />
                    <p className="text-white font-semibold text-sm sm:text-base flex items-center gap-1.5">
                      {h.title}
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>

        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default ContentHubPage;
